import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useMemo } from 'react';
import { addDays, startOfDay, endOfDay } from 'date-fns';

export interface NextTraining {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  modality: string;
  type: string;
  class_type: string;
  description?: string;
}

export const useNextTrainings = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['next-trainings', user?.id],
    queryFn: async (): Promise<NextTraining[]> => {
      if (!user) return [];

      try {
        // Get the current time to find next trainings from now onwards
        const now = new Date();
        
        // Fetch the next 3 scheduled trainings/classes (including classes) from now onwards
        // This ensures we get trainings regardless of which day they're scheduled
        const { data, error } = await supabase
          .from('trainings')
          .select(`
            id,
            title,
            start_time,
            end_time,
            modality,
            type,
            class_type,
            description
          `)
          .eq('user_id', user.id)
          .eq('status', 'Scheduled')
          .gte('start_time', now.toISOString()) // From now onwards
          .order('start_time', { ascending: true })
          .limit(3);

        if (error) {
          console.error('Error fetching next trainings:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('Error in useNextTrainings:', error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
